import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { formatDate } from "../../utils/dateUtils";
import alertService from "../../utils/alertService";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import DatePicker from "../../components/common/DatePicker";
import TextArea from "../../components/common/TextArea";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import ErrorAlert from "../../components/common/ErrorAlert";
import purchasePaymentService from "../../services/purchasePaymentService";
import paymentModeService from "../../services/paymentModeService";
import { API_CONFIG } from "../../utils/apiConfig";
import { getCurrencySymbol } from "../../utils/currency";

function getTodayDateString() {
  const today = new Date();
  const year = String(today.getFullYear());
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function PurchasePaymentDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: pmId } = useEncryptedParams();
  const basePath = "/purchase-payment-master";

  // --- States ---
  const [pmData, setPmData] = useState(location.state?.pm || null);
  const currencySymbol = useMemo(() => {
    if (!pmData) return "$";
    return pmData.currency?.symbol || getCurrencySymbol(pmData.currency?.currencyCode) || "$";
  }, [pmData]);
  const [payments, setPayments] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingTransaction, setIsSubmittingTransaction] = useState(false);
  const [error, setError] = useState(null);

  // Form State for Payment Transaction (Add/Edit)
  const [isEditingTransaction, setIsEditingTransaction] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [transactionForm, setTransactionForm] = useState({
    amount: "",
    date: getTodayDateString(),
    paymentModeId: "",
    currencyRate: "",
    remarks: "",
    isActive: true,
    bankName: "",
    accountNo: "",
    ifscCode: "",
    transactionRefNo: "",
    vpa: "",
    chequeNo: "",
    chequeDate: getTodayDateString(),
    attachment: null,
    attachmentPath: ""
  });
  const [formErrors, setFormErrors] = useState({});

  // Keep the form course rate and date in sync with the purchase master details
  useEffect(() => {
    if (!pmData) return;
    setTransactionForm((prev) => {
      let updatedDate = prev.date;
      if (pmData.billDate) {
        const currentFormDate = new Date(prev.date);
        const billDateVal = new Date(pmData.billDate);
        currentFormDate.setHours(0, 0, 0, 0);
        billDateVal.setHours(0, 0, 0, 0);
        if (currentFormDate < billDateVal) {
          updatedDate = pmData.billDate.split("T")[0];
        }
      }
      return {
        ...prev,
        currencyRate: prev.currencyRate || String(pmData.dollarRate || ""),
        date: updatedDate,
      };
    });
  }, [pmData]);

  // Load Purchase Master and Payments
  const loadAllData = useCallback(async () => {
    if (!pmId) return;
    setIsLoading(true);
    setError(null);
    try {
      // 1. Fetch payments
      const paymentsData = await purchasePaymentService.getPaymentsByPurchaseMasterId(pmId);
      setPayments(paymentsData);

      // 2. Fetch/resolve Purchase Master details if not in state
      if (!pmData) {
        const response = await purchasePaymentService.getPurchasePayments({ limit: 1000 });
        const matched = (response.items || []).find((item) => String(item.pmId) === String(pmId));
        if (matched) {
          setPmData(matched);
        } else {
          setError("Purchase Master record not found.");
        }
      } else {
        // If pmData was in state, refresh its paid/remaining values from the fresh payments list
        const newPaid = paymentsData.reduce((sum, p) => sum + Number(p.amount), 0);
        setPmData((prev) => ({
          ...prev,
          paidAmount: newPaid,
          remainingAmount: Number((prev.totalPaymentAmount - newPaid).toFixed(2)),
        }));
      }
    } catch (err) {
      setError(err.message || "Failed to load data.");
    } finally {
      setIsLoading(false);
    }
  }, [pmId, pmData]);

  // Load Payment Modes
  useEffect(() => {
    const loadModes = async () => {
      try {
        const modes = await paymentModeService.getActivePaymentModes();
        setPaymentModes(
          modes.map((m) => ({
            label: m.paymentMode,
            value: String(m.id),
            systemKey: m.systemKey
          }))
        );
      } catch (err) {
        console.error("Failed to load payment modes", err);
      }
    };
    loadModes();
  }, []);

  // Initial Load
  useEffect(() => {
    if (!pmId) {
      navigate(basePath);
      return;
    }
    loadAllData();
  }, [pmId, loadAllData, navigate, basePath]);

  // Reset Form
  const resetTransactionForm = () => {
    let defaultDate = getTodayDateString();
    if (pmData?.billDate) {
      const today = new Date(defaultDate);
      const bill = new Date(pmData.billDate);
      today.setHours(0, 0, 0, 0);
      bill.setHours(0, 0, 0, 0);
      if (today < bill) {
        defaultDate = pmData.billDate.split("T")[0];
      }
    }

    setTransactionForm({
      amount: "",
      date: defaultDate,
      paymentModeId: "",
      currencyRate: String(pmData?.dollarRate || ""),
      remarks: "",
      isActive: true,
      bankName: "",
      accountNo: "",
      ifscCode: "",
      transactionRefNo: "",
      vpa: "",
      chequeNo: "",
      chequeDate: getTodayDateString(),
      attachment: null,
      attachmentPath: ""
    });
    setFormErrors({});
    setIsEditingTransaction(false);
    setEditingTransactionId(null);
    const fileInput = document.getElementById("payment-attachment-input");
    if (fileInput) fileInput.value = "";
  };

  // Handle Form Change
  const handleFormChange = (field, value) => {
    let val = value;
    if (field === "amount" || field === "currencyRate") {
      if (typeof val === "string" && val.includes("-")) {
        val = val.replace(/-/g, "");
      }
      if (typeof val === "string" && val.includes(".")) {
        const parts = val.split(".");
        if (parts[1] && parts[1].length > 2) {
          val = `${parts[0]}.${parts[1].slice(0, 2)}`;
        }
      }
    }
    if (field === "attachment" && val) {
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
      const maxSize = 5 * 1024 * 1024; // 5 MB
      if (!allowedTypes.includes(val.type)) {
        alertService.error("Only PDF, JPG, and PNG files are allowed.");
        val = null;
        const fileInput = document.getElementById("payment-attachment-input");
        if (fileInput) fileInput.value = "";
      } else if (val.size > maxSize) {
        alertService.error("Maximum file size allowed is 5 MB.");
        val = null;
        const fileInput = document.getElementById("payment-attachment-input");
        if (fileInput) fileInput.value = "";
      }
    }
    if (field === "chequeNo") {
      val = val.replace(/[^a-zA-Z0-9]/g, "");
      if (val.length > 10) {
        val = val.slice(0, 10);
      }
    }
    if (field === "vpa") {
      // Remove spaces and any characters that are not alphanumeric, @, ., -, or _
      val = val.replace(/[^a-zA-Z0-9@.\-_]/g, "");
      // Keep only the first @ and strip any subsequent @
      const atCount = (val.match(/@/g) || []).length;
      if (atCount > 1) {
        const firstAtIdx = val.indexOf("@");
        val = val.slice(0, firstAtIdx + 1) + val.slice(firstAtIdx + 1).replace(/@/g, "");
      }
    }
    if (field === "accountNo") {
      val = val.replace(/[^a-zA-Z0-9]/g, "");
      if (val.length > 20) {
        val = val.slice(0, 20);
      }
    }
    if (field === "bankName" && val.length > 80) {
      val = val.slice(0, 80);
    }
    if (field === "ifscCode") {
      val = val.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
      if (val.length > 14) {
        val = val.slice(0, 14);
      }
    }
    setTransactionForm((prev) => ({ ...prev, [field]: val }));
    setFormErrors((prev) => ({ ...prev, [field]: null }));
  };

  // Handle Edit Transaction
  const handleEditTransaction = (transaction) => {
    setIsEditingTransaction(true);
    setEditingTransactionId(transaction.id);
    setTransactionForm({
      amount: String(transaction.amount),
      date: transaction.date ? transaction.date.split("T")[0] : getTodayDateString(),
      paymentModeId: String(transaction.paymentModeId),
      currencyRate: String(transaction.currencyRate ?? transaction.dollarRate ?? pmData?.dollarRate ?? ""),
      remarks: transaction.remarks || transaction.remark || "",
      isActive: Boolean(transaction.isActive),
      bankName: transaction.bankName || "",
      accountNo: transaction.accountNo || "",
      ifscCode: transaction.ifscCode || "",
      transactionRefNo: transaction.transactionRefNo || "",
      vpa: transaction.vpa || "",
      chequeNo: transaction.chequeNo || "",
      chequeDate: transaction.chequeDate ? transaction.chequeDate.split("T")[0] : getTodayDateString(),
      attachment: null,
      attachmentPath: transaction.attachmentPath || ""
    });
    setFormErrors({});
  };

  // Handle Delete Transaction
  const handleDeleteTransaction = async (transaction) => {
    const selectedMode = transaction.paymentMode;
    const modeLabel = selectedMode ? selectedMode.paymentMode : `Mode #${transaction.paymentModeId}`;
    const sysKey = selectedMode ? selectedMode.systemKey : "";
    const rate = Number(transaction.currencyRate ?? transaction.dollarRate ?? (pmData?.dollarRate || 1));

    let paymentDetailsStr = "";
    if (sysKey === "bank") {
      paymentDetailsStr = `Bank: ${transaction.bankName || "N/A"} | UTR: ${transaction.transactionRefNo || "N/A"}`;
    } else if (sysKey === "upi") {
      paymentDetailsStr = `UPI: ${transaction.vpa || "N/A"} | ID: ${transaction.transactionRefNo || "N/A"}`;
    } else if (sysKey === "cheque") {
      paymentDetailsStr = `Cheque: #${transaction.chequeNo || "N/A"} | Date: ${transaction.chequeDate ? formatDate(transaction.chequeDate) : "N/A"}`;
    } else if (sysKey === "cash") {
      paymentDetailsStr = `Cash | Bank: ${transaction.bankName || "N/A"} | A/C: ${transaction.accountNo || "N/A"}`;
    } else if (transaction.bankName || transaction.accountNo) {
      paymentDetailsStr = `Bank: ${transaction.bankName || "N/A"} | A/C: ${transaction.accountNo || "N/A"}`;
    }

    const details = {
      date: formatDate(transaction.date),
      paymentMode: modeLabel,
      formattedAmount: `${currencySymbol}${Number(transaction.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      currencyRate: rate.toFixed(2),
      amountInRs: `₹${Number(transaction.amount * rate).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      paymentDetails: paymentDetailsStr || null,
      remarks: transaction.remarks || transaction.remark || null
    };

    const confirm = await alertService.confirmPaymentAction(details, "delete");
    if (!confirm.isConfirmed) return;

    try {
      await purchasePaymentService.deletePurchasePayment(transaction.id);
      alertService.success("Payment transaction deleted successfully.");

      // Reload payments
      const updatedPayments = await purchasePaymentService.getPaymentsByPurchaseMasterId(pmId);
      setPayments(updatedPayments);

      // Update local PM stats
      const newPaid = updatedPayments.reduce((sum, p) => sum + Number(p.amount), 0);
      setPmData((prev) => ({
        ...prev,
        paidAmount: newPaid,
        remainingAmount: Number((prev.totalPaymentAmount - newPaid).toFixed(2)),
      }));
    } catch (err) {
      alertService.error(err.message || "Failed to delete transaction.");
    }
  };

  // Handle Submit
  const handleSubmitTransaction = async (e) => {
    e.preventDefault();
    setFormErrors({});

    const errors = {};
    if (!transactionForm.amount || parseFloat(transactionForm.amount) <= 0) {
      errors.amount = "Amount must be greater than 0";
    }
    if (!transactionForm.date) {
      errors.date = "Date is required";
    } else if (pmData?.billDate) {
      const paymentDateVal = new Date(transactionForm.date);
      const billDateVal = new Date(pmData.billDate);
      paymentDateVal.setHours(0, 0, 0, 0);
      billDateVal.setHours(0, 0, 0, 0);
      if (paymentDateVal < billDateVal) {
        errors.date = `Payment date cannot be earlier than the bill date (${formatDate(pmData.billDate)})`;
      }
    }
    if (!transactionForm.paymentModeId) {
      errors.paymentModeId = "Payment Mode is required";
    }
    if (!transactionForm.currencyRate || parseFloat(transactionForm.currencyRate) < 1.00) {
      errors.currencyRate = "Currency rate cannot be less than 1.00";
    }


    // Validate limit
    if (pmData && transactionForm.amount) {
      const amountNum = parseFloat(transactionForm.amount);
      const currentPaid = payments.reduce((sum, p) => {
        if (isEditingTransaction && p.id === editingTransactionId) return sum;
        return sum + Number(p.amount);
      }, 0);

      const maxAllowed = pmData.totalPaymentAmount - currentPaid;
      if (amountNum > maxAllowed) {
        errors.amount = `Amount cannot exceed the remaining payable limit of ${currencySymbol}${maxAllowed.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
      }
    }

    const selectedMode = paymentModes.find(m => String(m.value) === String(transactionForm.paymentModeId));
    const sysKey = selectedMode ? selectedMode.systemKey : "";

    if (sysKey === "cheque" && transactionForm.chequeNo) {
      if (/[^a-zA-Z0-9]/.test(transactionForm.chequeNo)) {
        errors.chequeNo = "Cheque number cannot contain special characters";
      } else if (transactionForm.chequeNo.length > 10) {
        errors.chequeNo = "Cheque number cannot exceed 10 characters";
      }
    }
    if (sysKey !== "upi" && transactionForm.bankName && transactionForm.bankName.length > 80) {
      errors.bankName = "Bank name cannot exceed 80 characters";
    }
    if (sysKey !== "upi" && sysKey !== "cheque" && transactionForm.accountNo && /[^a-zA-Z0-9]/.test(transactionForm.accountNo)) {
      errors.accountNo = "Account number cannot contain spaces or special characters";
    } else if (sysKey !== "upi" && sysKey !== "cheque" && transactionForm.accountNo && transactionForm.accountNo.length > 20) {
      errors.accountNo = "Account number cannot exceed 20 characters";
    }
    if (sysKey === "bank" && transactionForm.ifscCode && /[^a-zA-Z0-9]/.test(transactionForm.ifscCode)) {
      errors.ifscCode = "IFSC code cannot contain spaces or special characters";
    } else if (sysKey === "bank" && transactionForm.ifscCode && transactionForm.ifscCode.length > 14) {
      errors.ifscCode = "IFSC code cannot exceed 14 characters";
    }
    if (sysKey === "upi") {
      const vpaVal = transactionForm.vpa?.trim();
      if (!vpaVal) {
        errors.vpa = "UPI ID (VPA) is required";
      } else {
        const upiRegex = /^[a-zA-Z0-9.\-_]+@[a-zA-Z0-9.\-_]+$/;
        if (!upiRegex.test(vpaVal)) {
          errors.vpa = "Invalid UPI ID format (e.g., username@bank)";
        }
      }
      if (!transactionForm.transactionRefNo?.trim()) {
        errors.transactionRefNo = "Transaction ID is required";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      pmId: Number(pmId),
      amount: Number(transactionForm.amount),
      date: transactionForm.date,
      paymentModeId: Number(transactionForm.paymentModeId),
      currencyRate: Number(transactionForm.currencyRate),
      remarks: transactionForm.remarks?.trim() || "",
      isActive: Boolean(transactionForm.isActive),
      // Strategy B dynamic fields mapping
      bankName: sysKey !== "upi" ? transactionForm.bankName?.trim() || null : null,
      accountNo: (sysKey !== "upi" && sysKey !== "cheque") ? transactionForm.accountNo?.trim() || null : null,
      ifscCode: (sysKey === "bank") ? transactionForm.ifscCode?.trim() || null : null,
      transactionRefNo: (sysKey === "bank" || sysKey === "upi") ? transactionForm.transactionRefNo?.trim() || null : null,
      vpa: (sysKey === "upi") ? transactionForm.vpa?.trim() || null : null,
      chequeNo: (sysKey === "cheque") ? transactionForm.chequeNo?.trim() || null : null,
      chequeDate: (sysKey === "cheque") ? new Date(transactionForm.chequeDate).toISOString() : null,
      attachment: transactionForm.attachment || null,
      attachmentPath: transactionForm.attachmentPath || ""
    };

    const modeLabel = selectedMode ? selectedMode.label : "N/A";
    let paymentDetailsStr = "";
    if (sysKey === "bank") {
      paymentDetailsStr = `
        <div><strong>Bank:</strong> ${transactionForm.bankName || "N/A"}</div>
        <div><strong>A/C:</strong> ${transactionForm.accountNo || "N/A"}</div>
        <div><strong>IFSC:</strong> ${transactionForm.ifscCode || "N/A"}</div>
        <div><strong>UTR:</strong> ${transactionForm.transactionRefNo || "N/A"}</div>
      `;
    } else if (sysKey === "upi") {
      paymentDetailsStr = `
        <div><strong>UPI ID:</strong> ${transactionForm.vpa || "N/A"}</div>
        <div><strong>Txn ID:</strong> ${transactionForm.transactionRefNo || "N/A"}</div>
      `;
    } else if (sysKey === "cheque") {
      paymentDetailsStr = `
        <div><strong>Cheque:</strong> #${transactionForm.chequeNo || "N/A"}</div>
        <div><strong>Date:</strong> ${transactionForm.chequeDate ? formatDate(transactionForm.chequeDate) : "N/A"}</div>
        <div><strong>Bank:</strong> ${transactionForm.bankName || "N/A"}</div>
      `;
    } else if (sysKey === "cash") {
      paymentDetailsStr = `
        <div><strong>Cash</strong></div>
        <div><strong>Bank:</strong> ${transactionForm.bankName || "N/A"}</div>
        ${transactionForm.accountNo ? `<div><strong>A/C:</strong> ${transactionForm.accountNo}</div>` : ""}
      `;
    } else if (transactionForm.bankName || transactionForm.accountNo) {
      paymentDetailsStr = `
        <div><strong>Bank:</strong> ${transactionForm.bankName || "N/A"}</div>
        <div><strong>A/C:</strong> ${transactionForm.accountNo || "N/A"}</div>
      `;
    }

    const details = {
      date: formatDate(transactionForm.date),
      paymentMode: modeLabel,
      formattedAmount: `${currencySymbol}${Number(transactionForm.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      currencyRate: Number(transactionForm.currencyRate).toFixed(2),
      amountInRs: `₹${Number(parseFloat(transactionForm.amount) * parseFloat(transactionForm.currencyRate)).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      paymentDetails: paymentDetailsStr || null,
      remarks: transactionForm.remarks?.trim() || null
    };

    const actionType = isEditingTransaction ? "update" : "save";
    const confirm = await alertService.confirmPaymentAction(details, actionType);
    if (!confirm.isConfirmed) return;

    setIsSubmittingTransaction(true);
    try {
      if (isEditingTransaction) {
        await purchasePaymentService.updatePurchasePayment(editingTransactionId, payload);
        alertService.success("Payment transaction updated successfully.");
      } else {
        await purchasePaymentService.createPurchasePayment(payload);
        alertService.success("Payment transaction recorded successfully.");
      }
      resetTransactionForm();

      // Reload payments
      const updatedPayments = await purchasePaymentService.getPaymentsByPurchaseMasterId(pmId);
      setPayments(updatedPayments);

      // Update local PM stats
      const newPaid = updatedPayments.reduce((sum, p) => sum + Number(p.amount), 0);
      setPmData((prev) => ({
        ...prev,
        paidAmount: newPaid,
        remainingAmount: Number((prev.totalPaymentAmount - newPaid).toFixed(2)),
      }));
    } catch (err) {
      alertService.error(err.message || "Failed to save payment transaction.");
    } finally {
      setIsSubmittingTransaction(false);
    }
  };

  if (isLoading && !pmData) {
    return <div className="p-10 text-center text-slate-500">Loading payment details...</div>;
  }

  return (
    <div className="space-y-4 pb-8 px-4 sm:px-0">
      {/* --- Header --- */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate(basePath)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
              Manage <span className="text-primary-600">Payments</span>
            </h1>
          </div>
          {pmData && (
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-y-1.5 gap-x-3 text-xs font-bold text-slate-500 dark:text-slate-400 md:pl-9 uppercase tracking-widest">
              <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 w-fit">
                Bill No: {pmData.billNo || "N/A"}
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="text-primary-600">Vendor Name: {pmData.vendorName || "N/A"}</span>
              <span className="hidden sm:inline">•</span>
              <span>Date: {formatDate(pmData.billDate)}</span>
            </div>
          )}
        </div>
      </div>

      {error && <ErrorAlert message={error} />}

      {pmData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Card 1: Total Payable */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Total Payable</p>
                <h3 className="text-xl font-black text-slate-800 dark:text-white font-mono">
                  {currencySymbol}{Number(pmData.totalPaymentAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </h3>
                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  ₹{Number(pmData.totalPaymentAmountinRs).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-500 dark:bg-slate-800">
                <i className="material-icons text-xl">account_balance_wallet</i>
              </div>
            </div>

            {/* Card 2: Total Paid */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Total Paid</p>
                <h3 className="text-xl font-black text-emerald-600 dark:text-emerald-400 font-mono">
                  {currencySymbol}{Number(pmData.paidAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </h3>
                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  ₹{Number(pmData.paidAmount * (pmData.dollarRate || 1)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400">
                <i className="material-icons text-xl">check_circle</i>
              </div>
            </div>

            {/* Card 3: Remaining Balance */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Remaining Balance</p>
                <h3 className={`text-xl font-black font-mono ${Number(pmData.remainingAmount) <= 0 ? "text-slate-400" : "text-amber-600"}`}>
                  {currencySymbol}{Number(pmData.remainingAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </h3>
                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  ₹{Number(pmData.remainingAmount * (pmData.dollarRate || 1)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${Number(pmData.remainingAmount) <= 0 ? "bg-slate-50 text-slate-400" : "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400"}`}>
                <i className="material-icons text-xl">pending_actions</i>
              </div>
            </div>
          </div>

          {/* Grid Split Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Side: Payment History */}
            <div className="lg:col-span-8 space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-slate-50/50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <h2 className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-3">
                    <i className="material-icons text-sm">history</i>
                    Transaction History
                    <span className="px-2 py-0.5 rounded-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-[10px]">
                      {payments.length} Records
                    </span>
                  </h2>
                </div>

                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-slate-50/30 dark:bg-slate-800/30 text-[10px] font-black uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-800 whitespace-nowrap">
                        <th className="px-5 py-3 text-center">Sr. No.</th>
                        <th className="px-5 py-3">Date</th>
                        <th className="px-5 py-3">Payment Mode</th>
                        <th className="px-5 py-3 text-right">Amount</th>
                        <th className="px-5 py-3 text-right">Currency Rate ({currencySymbol})</th>
                        <th className="px-5 py-3 text-right">Amount (₹)</th>
                        <th className="px-5 py-3 min-w-[250px]">Details</th>
                        <th className="px-5 py-3 text-center">Receipt</th>
                        <th className="px-5 py-3">Remarks</th>
                        <th className="px-5 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {payments.length === 0 ? (
                        <tr>
                          <td colSpan="11" className="px-5 py-12 text-center text-slate-400 italic">
                            No payment transactions recorded for this purchase yet.
                          </td>
                        </tr>
                      ) : (
                        payments.map((p, index) => {
                          const rate = Number(p.currencyRate ?? p.dollarRate ?? (pmData.dollarRate || 1));
                          const remarksText = p.remarks || p.remark || "-";
                          return (
                            <tr key={p.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                              <td className="px-5 py-3 text-center font-medium">{index + 1}</td>
                              <td className="px-5 py-3 font-medium">{formatDate(p.date)}</td>
                              <td className="px-5 py-3 whitespace-nowrap">
                                <span className="px-2.5 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-[11px] font-bold whitespace-nowrap">
                                  {p.paymentMode?.paymentMode || `Mode #${p.paymentModeId}`}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-right font-mono font-black text-slate-800 dark:text-white">
                                {currencySymbol}{Number(p.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-5 py-3 text-right font-mono text-slate-500">
                                {rate.toFixed(2)}
                              </td>

                              <td className="px-5 py-3 text-right font-mono text-slate-500">
                                ₹{Number(p.amount * rate).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-5 py-3 text-slate-600 dark:text-slate-300 font-medium min-w-[250px]">
                                {(() => {
                                  const key = p.paymentMode?.systemKey || "";
                                  if (key === "bank") {
                                    return (
                                      <div className="flex flex-col gap-0.5 text-xs py-1">
                                        <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.bankName || "N/A"}</span></div>
                                        <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.accountNo || "N/A"}</span></div>
                                        <div><span className="font-bold text-slate-400 dark:text-slate-500">IFSC:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.ifscCode || "N/A"}</span></div>
                                        <div><span className="font-bold text-slate-400 dark:text-slate-500">UTR:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.transactionRefNo || "N/A"}</span></div>
                                      </div>
                                    );
                                  } else if (key === "upi") {
                                    return (
                                      <div className="flex flex-col gap-0.5 text-xs py-1">
                                        <div><span className="font-bold text-slate-400 dark:text-slate-500">UPI:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.vpa || "N/A"}</span></div>
                                        <div><span className="font-bold text-slate-400 dark:text-slate-500">ID:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.transactionRefNo || "N/A"}</span></div>
                                      </div>
                                    );
                                  } else if (key === "cheque") {
                                    return (
                                      <div className="flex flex-col gap-0.5 text-xs py-1">
                                        <div><span className="font-bold text-slate-400 dark:text-slate-500">Cheque:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">#{p.chequeNo || "N/A"}</span></div>
                                        <div><span className="font-bold text-slate-400 dark:text-slate-500">Date:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.chequeDate ? formatDate(p.chequeDate) : "N/A"}</span></div>
                                        <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.bankName || "N/A"}</span></div>
                                      </div>
                                    );
                                  } else if (key === "cash") {
                                    return (
                                      <div className="flex flex-col gap-0.5 text-xs py-1">
                                        <div className="font-bold text-slate-500 dark:text-slate-400">Cash</div>
                                        {p.bankName && <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.bankName}</span></div>}
                                        {p.accountNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.accountNo}</span></div>}
                                      </div>
                                    );
                                  }
                                  if (p.bankName || p.accountNo) {
                                    return (
                                      <div className="flex flex-col gap-0.5 text-xs py-1">
                                        {p.bankName && <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.bankName}</span></div>}
                                        {p.accountNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.accountNo}</span></div>}
                                      </div>
                                    );
                                  }
                                  return "N/A";
                                })()}
                              </td>
                              <td className="px-5 py-3 text-center">
                                {p.attachmentPath ? (
                                  <a
                                    href={`${API_CONFIG.baseUrl}/${p.attachmentPath.replace(/\\/g, "/")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-bold hover:underline"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    View
                                  </a>
                                ) : (
                                  <span className="text-slate-400">N/A</span>
                                )}
                              </td>
                              <td className="px-5 py-3 text-sm text-slate-600 dark:text-slate-300 max-w-[200px]">
                                <div className="max-w-[200px] overflow-x-auto whitespace-nowrap custom-scrollbar" title={remarksText}>
                                  {remarksText}
                                </div>
                              </td>
                              <td className="px-5 py-3 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleEditTransaction(p)}
                                    className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-all"
                                    title="Edit"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTransaction(p)}
                                    className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-all"
                                    title="Delete"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile View: Cards */}
                <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {payments.length === 0 ? (
                    <div className="px-5 py-8 text-center text-slate-400 italic">
                      No payment transactions recorded for this purchase yet.
                    </div>
                  ) : (
                    payments.map((p, index) => {
                      const rate = Number(p.currencyRate ?? p.dollarRate ?? (pmData.dollarRate || 1));
                      const remarksText = p.remarks || p.remark || "N/A";
                      const detailsContent = (() => {
                        const key = p.paymentMode?.systemKey || "";
                        if (key === "bank") {
                          return (
                            <div className="flex flex-col gap-0.5">
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.bankName || "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.accountNo || "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">IFSC:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.ifscCode || "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">UTR:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.transactionRefNo || "N/A"}</span></div>
                            </div>
                          );
                        } else if (key === "upi") {
                          return (
                            <div className="flex flex-col gap-0.5">
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">UPI:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.vpa || "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">ID:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.transactionRefNo || "N/A"}</span></div>
                            </div>
                          );
                        } else if (key === "cheque") {
                          return (
                            <div className="flex flex-col gap-0.5">
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">Cheque:</span> <span className="font-medium text-slate-700 dark:text-slate-300">#{p.chequeNo || "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">Date:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.chequeDate ? formatDate(p.chequeDate) : "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.bankName || "N/A"}</span></div>
                            </div>
                          );
                        } else if (key === "cash") {
                          return (
                            <div className="flex flex-col gap-0.5">
                              <div className="font-bold text-slate-500 dark:text-slate-400">Cash</div>
                              {p.bankName && <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.bankName}</span></div>}
                              {p.accountNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.accountNo}</span></div>}
                            </div>
                          );
                        }
                        if (p.bankName || p.accountNo) {
                          return (
                            <div className="flex flex-col gap-0.5">
                              {p.bankName && <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.bankName}</span></div>}
                              {p.accountNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.accountNo}</span></div>}
                            </div>
                          );
                        }
                        return null;
                      })();

                      return (
                        <div key={p.id} className="p-4 space-y-3 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[10px] font-black text-slate-500">
                                {index + 1}
                              </span>
                              <span className="font-bold text-slate-500">{formatDate(p.date)}</span>
                            </div>
                            <span className="px-2 py-0.5 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-[10px] font-bold">
                              {p.paymentMode?.paymentMode || `Mode #${p.paymentModeId}`}
                            </span>
                          </div>

                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500 font-bold">Amount:</span>
                            <span className="font-black text-slate-800 dark:text-white font-mono">
                              {currencySymbol}{Number(p.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </span>
                          </div>

                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500 font-bold">Currency Rate ({currencySymbol}):</span>
                            <span className="font-mono text-slate-600 dark:text-slate-400">{rate.toFixed(2)}</span>
                          </div>

                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500 font-bold">Amount (₹):</span>
                            <span className="font-black text-emerald-600 dark:text-emerald-400 font-mono">
                              ₹{Number(p.amount * rate).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </span>
                          </div>

                          {detailsContent && (
                            <div className="text-[11px] text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800">
                              {detailsContent}
                            </div>
                          )}

                          {remarksText !== "-" && (
                            <div className="text-[11px] text-slate-500 italic">
                              <span className="font-bold text-slate-400">Remarks: </span>{remarksText}
                            </div>
                          )}

                          <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-2 mt-2">
                            <div>
                              {p.attachmentPath ? (
                                <a
                                  href={`${API_CONFIG.baseUrl}/${p.attachmentPath.replace(/\\/g, "/")}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] text-primary-600 hover:text-primary-700 font-bold"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  View Receipt
                                </a>
                              ) : (
                                <span className="text-[10px] text-slate-400 italic">No receipt</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditTransaction(p)}
                                className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-all text-xs flex items-center gap-1 border border-blue-200/50 dark:border-blue-950/50"
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteTransaction(p)}
                                className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-all text-xs flex items-center gap-1 border border-rose-200/50 dark:border-rose-950/50"
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Right Side: Form or Fully Paid Card */}
            <div className="lg:col-span-4">
              {Number(pmData.remainingAmount) <= 0 && !isEditingTransaction ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6 text-center dark:border-emerald-900/30 dark:bg-emerald-950/20 sticky top-6 animate-in zoom-in duration-300">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <i className="material-icons text-4xl text-emerald-500">task_alt</i>
                    <h3 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-tight">
                      Fully Paid
                    </h3>
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest leading-relaxed">
                      This purchase has been fully paid. No further payment transactions are required.
                    </p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-2">
                      To modify payments, click the edit or delete icons in the transaction history table.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sticky top-6">
                  <h2 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight mb-6 border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-[10px] text-white">
                      {isEditingTransaction ? "✎" : "+"}
                    </span>
                    {isEditingTransaction ? "Edit Payment" : "Add Payment"}
                  </h2>

                  <form onSubmit={handleSubmitTransaction} noValidate className="space-y-5">
                    <DatePicker
                      label="Payment Date"
                      required={true}
                      value={transactionForm.date}
                      onChange={(val) => handleFormChange("date", val)}
                      error={formErrors.date}
                      minDate={pmData?.billDate}
                      labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                    />

                    <SearchableDropdown
                      label={
                        <>
                          Payment Mode <span className="text-rose-500">*</span>
                        </>
                      }
                      value={transactionForm.paymentModeId}
                      options={paymentModes}
                      placeholder="Select Mode"
                      onChange={(e) => handleFormChange("paymentModeId", e.target.value)}
                      error={formErrors.paymentModeId}
                      labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                    />

                    <div className="space-y-1">
                      <Input
                        label={
                          <>
                            Currency Rate ({pmData.currency?.currencyCode || "USD"}) <span className="text-rose-500">*</span>
                          </>
                        }
                        type="number"
                        min="1.00"
                        step="0.01"
                        value={transactionForm.currencyRate}
                        onChange={(e) => handleFormChange("currencyRate", e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "E") {
                            e.preventDefault();
                          }
                        }}
                        placeholder={String(pmData.dollarRate || "")}
                        error={formErrors.currencyRate}
                        className="font-bold font-mono"
                        labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                      />
                    </div>

                    <div className="space-y-1">
                      <Input
                        label={
                          <>
                            Amount <span className="text-rose-500">*</span>
                          </>
                        }
                        type="number"
                        min="0"
                        value={transactionForm.amount}
                        onChange={(e) => handleFormChange("amount", e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "E") {
                            e.preventDefault();
                          }
                        }}
                        placeholder="0.00"
                        error={formErrors.amount}
                        className="font-bold font-mono"
                        labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                      />
                      {transactionForm.amount && !isNaN(parseFloat(transactionForm.amount)) && (
                        <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-tight pl-1">
                          Equivalent INR: ₹{Number(parseFloat(transactionForm.amount) * (parseFloat(transactionForm.currencyRate || pmData.dollarRate || 1))).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </p>
                      )}
                    </div>

                    {/* --- Dynamic Payment Fields (Strategy B) --- */}
                    {(() => {
                      const selectedMode = paymentModes.find(m => String(m.value) === String(transactionForm.paymentModeId));
                      const sysKey = selectedMode ? (selectedMode.systemKey || "") : "";
                      if (!transactionForm.paymentModeId) return null;

                      return (
                        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 p-4 space-y-4 shadow-sm">
                          <div className="text-[10px] font-black text-primary-600 dark:text-primary-400 uppercase tracking-widest flex items-center gap-1.5 border-b border-slate-200/60 dark:border-slate-800 pb-2 mb-1">
                            <i className="material-icons text-sm">info</i>
                            {selectedMode?.label || "Payment"} Details
                          </div>
                          {/* Bank Name */}
                          {sysKey !== "upi" && (
                            <div className="space-y-1">
                              <Input
                                label={sysKey === "cheque" ? "Drawn On (Bank)" : "Bank Name"}
                                type="text"
                                value={transactionForm.bankName}
                                onChange={(e) => handleFormChange("bankName", e.target.value)}
                                placeholder={sysKey === "cheque" ? "Bank name" : "e.g., SBI, HDFC"}
                                error={formErrors.bankName}
                                maxLength={80}
                                labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                              />
                            </div>
                          )}

                          {/* Account Number */}
                          {(sysKey !== "upi" && sysKey !== "cheque") && (
                            <div className="space-y-1">
                              <Input
                                label="Account Number"
                                type="text"
                                value={transactionForm.accountNo}
                                onChange={(e) => handleFormChange("accountNo", e.target.value)}
                                placeholder="Account number"
                                error={formErrors.accountNo}
                                maxLength={20}
                                labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                              />
                            </div>
                          )}

                          {/* IFSC Code */}
                          {sysKey === "bank" && (
                            <div className="space-y-1">
                              <Input
                                label="IFSC Code"
                                type="text"
                                value={transactionForm.ifscCode}
                                onChange={(e) => handleFormChange("ifscCode", e.target.value)}
                                placeholder="IFSC code"
                                error={formErrors.ifscCode}
                                maxLength={14}
                                labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                              />
                            </div>
                          )}

                          {/* UPI ID (VPA) */}
                          {sysKey === "upi" && (
                            <div className="space-y-1">
                              <Input
                                label="UPI ID (VPA) *"
                                type="text"
                                value={transactionForm.vpa}
                                onChange={(e) => handleFormChange("vpa", e.target.value)}
                                placeholder="e.g., username@upi"
                                error={formErrors.vpa}
                                labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                              />
                            </div>
                          )}

                          {/* Transaction Ref ID / UTR */}
                          {(sysKey === "bank" || sysKey === "upi") && (
                            <div className="space-y-1">
                              <Input
                                label={sysKey === "bank" ? "UTR / Ref No" : "Transaction ID *"}
                                type="text"
                                value={transactionForm.transactionRefNo}
                                onChange={(e) => handleFormChange("transactionRefNo", e.target.value)}
                                placeholder={sysKey === "bank" ? "UTR reference number" : "UPI reference ID"}
                                error={formErrors.transactionRefNo}
                                labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                              />
                            </div>
                          )}

                          {/* Cheque Number */}
                          {sysKey === "cheque" && (
                            <div className="space-y-1">
                              <Input
                                label="Cheque Number"
                                type="text"
                                value={transactionForm.chequeNo}
                                onChange={(e) => handleFormChange("chequeNo", e.target.value)}
                                placeholder="Cheque number"
                                error={formErrors.chequeNo}
                                maxLength={10}
                                labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                              />
                            </div>
                          )}

                          {/* Cheque Date */}
                          {sysKey === "cheque" && (
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Cheque Date</label>
                              <DatePicker
                                value={transactionForm.chequeDate}
                                onChange={(val) => handleFormChange("chequeDate", val)}
                                error={formErrors.chequeDate}
                              />
                            </div>
                          )}

                          {/* Attachment File Upload & Preview */}
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Attachment</label>
                            {transactionForm.attachment || (transactionForm.attachmentPath && transactionForm.attachmentPath !== "") ? (
                              <div className="flex items-center justify-between py-1 px-2.5 bg-slate-50/40 dark:bg-slate-900/40 border border-slate-200/80 dark:border-slate-800 rounded-md shadow-sm animate-in fade-in duration-200">
                                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 truncate pr-2" title={
                                  transactionForm.attachment
                                    ? transactionForm.attachment.name
                                    : transactionForm.attachmentPath.split(/[\\/]/).pop()
                                }>
                                  {transactionForm.attachment
                                    ? transactionForm.attachment.name
                                    : transactionForm.attachmentPath.split(/[\\/]/).pop()}
                                </span>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (transactionForm.attachment) {
                                        const fileUrl = URL.createObjectURL(transactionForm.attachment);
                                        window.open(fileUrl, "_blank");
                                      } else if (transactionForm.attachmentPath) {
                                        window.open(`${API_CONFIG.baseUrl}/${transactionForm.attachmentPath.replace(/\\/g, "/")}`, "_blank");
                                      }
                                    }}
                                    className="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm active:scale-95"
                                    title="View Attachment"
                                  >
                                    <i className="material-icons text-amber-700 dark:text-amber-500" style={{ fontSize: "14px" }}>visibility</i>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setTransactionForm(prev => ({
                                        ...prev,
                                        attachment: null,
                                        attachmentPath: ""
                                      }));
                                      const fileInput = document.getElementById("payment-attachment-input");
                                      if (fileInput) fileInput.value = "";
                                    }}
                                    className="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm active:scale-95"
                                    title="Remove Attachment"
                                  >
                                    <i className="material-icons text-rose-500 dark:text-rose-400" style={{ fontSize: "14px" }}>close</i>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <input
                                id="payment-attachment-input"
                                type="file"
                                onChange={(e) => handleFormChange("attachment", e.target.files[0])}
                                accept="application/pdf,image/jpeg,image/png"
                                className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
                              />
                            )}
                            <p className="text-[10px] text-rose-500 font-semibold mt-1">
                              * Only PDF, JPG, PNG. Maximum Size: 5 MB
                            </p>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="space-y-1">
                      <TextArea
                        label="Remarks"
                        value={transactionForm.remarks}
                        onChange={(e) => handleFormChange("remarks", e.target.value)}
                        placeholder="Add remarks (optional)"
                        error={formErrors.remarks}
                        className="font-medium"
                        labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                        rows={2}
                      />
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button
                        type="submit"
                        className="flex-1"
                        isLoading={isSubmittingTransaction}
                      >
                        {isEditingTransaction ? "Update Payment" : "Save Payment"}
                      </Button>
                      {isEditingTransaction && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={resetTransactionForm}
                          className="text-slate-500"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PurchasePaymentDetailPage;
